import { TimedMessage } from "components/TimedMessage";
import { useProductFilter } from "hooks";
import { useEffect, useRef, useState } from "react";
import { Dropdown, Form, Modal } from "react-bootstrap";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { getProducts, clearSystemStocksState, updateProductField, clearFilters, addAGGridSelectedRows, addAGGrideDeselectedRows } from "redux/slices/Products";
import { RootState } from "redux/store";
import { cleanStockModel } from "redux/slices/Stocks";
import { AgGridToolbar, AgGridToolbarRef } from "components/AgGrid/AgGridToolbar";
import { createBooleanSelectFilter, createSingleSelectFilter, createDateSelectFilter } from "components/AgGrid/FilterComponents/Filters";
import { ScheduleCodes } from "constants/constants";
import { ManagedSupplier } from "models/dbModels/managedSuppliers/ManagedSupplier";
import { Z } from "models/common/components";
import { AgGridReact } from "ag-grid-react";
import { IServerSideDatasource, IServerSideSelectionState, RowSelectedEvent } from "ag-grid-community";
import { getSystemStockConfig } from "TableConfigs/SystemStocksConfig";
import { Product } from "models/dbModels/products/Product";
import DateCellRenderer from "components/AgGrid/CellRenderers/DateCellRenderer";
import { decodeToken, getAuthData } from "helpers/api/authService";
import { ProductSearchColumnSearchType, ProductsSearchManagedStockFilterType } from "models/filters/products/ProductsSearchFilterQuery";

type SystemStocksPopupProps = {
	show: boolean;
	onClose: () => void;
	onSave: () => void;
	genericCategoryId?: string;
	canSelectAll: boolean;
};

type SerchFilters = {

	CreateDateBeforeFilter: Date | null;
	CreateDateAfterFilter: Date | null;
};

export enum ProductSearchStatusType {
	None = "",
	Added = "Yes",
	NotAdded = "No",
}

export function SystemStocksPopup(props: SystemStocksPopupProps) {
	const dispatch = useAppDispatch();
	const { genericCategoryId } = props;
	const [saving, setSaving] = useState(false);
	const getProductFilterOptions = useProductFilter();
	const products = useAppSelector((state: RootState) => state.products);
	const toolbarRef = useRef<AgGridToolbarRef>(null);
	const suppliers = useAppSelector((state: RootState) => state.suppliers);
	const systemStockGridRef = useRef<AgGridReact>(null);
	const [serverSideSelectionState, setServerSideSelectionState] = useState<{ selectAll: boolean; toggledNodes: Product[] }>({ selectAll: false, toggledNodes: [] });
	const [selectableRowCount, setSelectableRowCount] = useState<number>(0);

	useEffect(() => {
		setSaving(false);
		dispatch(clearSystemStocksState());
		dispatch(cleanStockModel());
		dispatch(clearFilters());
		dispatch(updateProductField({ key: "managedStock", value: ProductSearchStatusType.NotAdded }));
	}, [props.show]);

	useEffect(()=>{		
		var datasource = getServerSideDatasource();
        systemStockGridRef.current?.api.setGridOption("serverSideDatasource", datasource);
	},[products.keyword])

	useEffect(() => {
		let rows = 0;		
		if (products.selectAllFilter) {
			let deselectedRows = 0;
			products.deselectedRows.forEach((row: any) => deselectedRows = deselectedRows + row.length);
			rows = products.totalRows - deselectedRows;
		} else {
			products.selectedRows.forEach((row: any) => rows = rows + row.length);
		}
	}, [products.selectedRows, products.deselectedRows]);

	function onGridReady(){
		systemStockGridRef.current?.api.deselectAll();
		onSearchClick("");
		var datasource = getServerSideDatasource();
        systemStockGridRef.current?.api.setGridOption("serverSideDatasource", datasource);
	}

	function onSearchClick(keyword: string) {

		if(keyword == products.keyword){
			var datasource = getServerSideDatasource();
			systemStockGridRef.current?.api.setGridOption("serverSideDatasource", datasource);
		}
		
		else {
			dispatch(updateProductField({ key: "keyword", value: keyword }));
		}	
		columnCheckDeselectAll();		
	}

/// Does the api call for report data. also handles grouping requests
	const getServerSideDatasource: () => IServerSideDatasource = () => {

		const rootGroupId = decodeToken()?.RootStoreGroup;

		return {
			getRows: async (params) => {
				let response: any = [];
				response = await dispatch(getProducts({
					...getProductFilterOptions(),
					keyword: products.keyword,
					offset: params.request.startRow,
					limit: params.request.endRow != undefined && params.request.startRow != undefined ? params.request.endRow - params.request.startRow : 100,
					searchPdes: products.searchPdes,
					searchBarcodes: products.searchBarcodes,
					loadApns: true,
					loadPdes: true,
					columnSearchType: ProductSearchColumnSearchType.QuickSearch,
					...(rootGroupId != null && {storeGroupIds: [rootGroupId], managedStockFilterType: ProductsSearchManagedStockFilterType.Mark}),
					...(products.managedStock === ProductSearchStatusType.NotAdded && { managedStockFilterType: ProductsSearchManagedStockFilterType.Exclude }),
					...(products.managedStock === ProductSearchStatusType.Added && {managedStockFilterType: ProductsSearchManagedStockFilterType.Only }),
					...(genericCategoryId && { genericCategoryIds: [genericCategoryId] })
				}));
				if (response?.data == null) {
                    params.api.showNoRowsOverlay();
                    params.success({
						rowData: [],
                        rowCount: 0,
                    });
                    return;
                }
				params.api.hideOverlay();
				setSelectableRowCount(products.managedStock !== ProductSearchStatusType.Added ? (response.totalSelectableRows ?? 0) : 0);
				params.success({
					rowData: response.data,
					rowCount: response.totalRows,
				});
			}
		}
	}

	function selectedRowsUpdateAgGrid(rows: any[]) {
		let formatedData:{
			positon: number,
			id: number
		} [] = []
		rows.forEach((xe: any) => formatedData.push({ positon: 0, id: xe.id}));
		dispatch(addAGGridSelectedRows([formatedData]));
	}

	function deselectedRowsUpdateAgGrid(rows: any[]) {		
			let formatedData:{
				positon: number,
				id: number
			} [] = []
			rows.forEach(xe => formatedData.push({ positon: 0, id: xe.id}));
			dispatch(addAGGrideDeselectedRows([formatedData]));
	}
	

	function handleChange(option: any, optionValue: any) {
		dispatch(updateProductField({ key: option, value: optionValue }));
	}

	function columnCheckDeselectAll() {
		handleChange("selectAllFilter", false);
		handleChange("deselectedRows", []);
		handleChange("selectedRows", []);
	}

	function columnCheckSelectAll() {
		handleChange("selectAllFilter", true);
		handleChange("deselectedRows", []);
	}

	function saveSelectedRows() {
		setSaving(true);
		props.onSave();
	}

	const CustomSearchButton = () => {
		return (
			<>
				<Dropdown.Item onClick={() => handleChange("searchPdes", !products.searchPdes)}>
					<Form.Check
						type="checkbox"
						label="PDE"
						checked={products.searchPdes}
						onChange={() => handleChange("searchPdes", !products.searchPdes)}
						onClick={e => e.stopPropagation()}
					/>
				</Dropdown.Item>
				<Dropdown.Item onClick={() => handleChange("searchBarcodes", !products.searchBarcodes)}>
					<Form.Check
						type="checkbox"
						label="APN"
						checked={products.searchBarcodes}
						onChange={() => handleChange("searchBarcodes", !products.searchBarcodes)}
						onClick={e => e.stopPropagation()}
					/>
				</Dropdown.Item>
			</>
		);
	}

	const getSelectedCount = () => {
		if (systemStockGridRef.current?.api && serverSideSelectionState.toggledNodes.length >= 1 && products.selectAllFilter != true)
		{
			return serverSideSelectionState.toggledNodes.length;
		} else if (products.selectAllFilter == true && products.deselectedRows.length == 0) {
			return selectableRowCount;
		} else if (products.selectAllFilter == true && products.deselectedRows.length > 0){
			return selectableRowCount - products.deselectedRows[0].length;
		}
		return 0;
	}

	return (
		<Modal size="xl" centered show={props.show} onHide={props.onClose} backdrop={saving ? "static" : true}>
			<Modal.Header closeButton={!saving}>
				<Modal.Title>System Stocks</Modal.Title>
			</Modal.Header>
			<Modal.Body className={`${saving && "disabled-div"}`}>
            	<TimedMessage message="Please wait while we add all your data..." delay={30} enabled={saving} />
				<AgGridToolbar
					search={true}
					ref={toolbarRef}
					className="my-2 px-1"
					handleSubmit={(searchState, filterState) => {
						onSearchClick(searchState?.keyword != undefined? searchState.keyword: "");
					}}
					handleClearFilters={()=>{
						dispatch(clearFilters());
					}}
					filters={[
						createSingleSelectFilter<string>({
							label: "Managed Stock",
							filterName: "managedStock",
							hasNoneOption: true,
							options: [ProductSearchStatusType.Added, ProductSearchStatusType.NotAdded],
							value: (option) => option,
							display: (option) => option,
							onFilterChange: function (e: string | undefined): void {
								dispatch(updateProductField({ key: "managedStock", value: e }));
							},
							defaultValue: ProductSearchStatusType.NotAdded
						}),			
						createSingleSelectFilter<ManagedSupplier>({
							label: "Supplier",
							filterName: "supplierIds",
							options: suppliers.table,
							value: (option) => [option.id],
							display: (option) => option.name,
							hasNoneOption: true,
							onFilterChange: function (e: ManagedSupplier | undefined): void {
								dispatch(updateProductField({ key: "supplierCodesFilter", value: e?.systemCode }));
							}
						}),			
						createBooleanSelectFilter({
							label: "Ethical",
							filterName: "ethical",
							hasNoneOption: true,
							onFilterChange: function (e: boolean | undefined): void {
								dispatch(updateProductField({ key: "ethicalFilter", value: e }));
							}
						}),
						createSingleSelectFilter<string>({
							label: "Schedule",
							filterName: "schedule",
							options: ScheduleCodes,
							value: (option) => option,
							display: (option) => option,
							hasNoneOption: true,
							onFilterChange: function (e: string | undefined): void {
								dispatch(updateProductField({ key: "scheduleFilter", value: e }));
							}
						}),
						createBooleanSelectFilter({
							label: "PBS",
							filterName: "pbs",
							hasNoneOption: true,
							onFilterChange: function (e: boolean | undefined): void {
								dispatch(updateProductField({ key: "pbsFilter", value: e }));
							} 
						}),
						createDateSelectFilter({
							label: "Created Before",
							filterName: "createdBefore",
							value: (option) => option,						
							onFilterChange: function (e: Date | null): void {
								dispatch(updateProductField({ key: "createDateBeforeFilter", value: e }));
							}
						}),
						createDateSelectFilter({
							label: "Created After",
							filterName: "createdAfter",
							value: (option) => option,
							onFilterChange: function (e: Date | null): void {
								dispatch(updateProductField({ key: "createDateAfterFilter", value: e }));							
							}
						})
					]}
					searchProps={
						{totalRows: products.totalRows, placeholder: "by Name" + (products.searchPdes == true ?" / PDE":"" ) + (products.searchBarcodes == true? " / APN" : ""), customSearch: CustomSearchButton(), hideContains: true}
					}
					children={(<div className="d-inline-flex gap-1">
									{ 
									(getSelectedCount() > 0) &&									
										<Z.Button variant="light" className="h-100" disabled={true} buttonText={getSelectedCount() + " Selected"}/>
									}
									{																							
										<Z.Button className="h-100" variant="primary" buttonText="Add Selected" disabled={getSelectedCount() === 0} onClick={saveSelectedRows}/>
									}
								</div>)}
				/>
				<AgGridReact
					ref={systemStockGridRef}
					columnDefs={getSystemStockConfig()}
					rowModelType="serverSide"
					pagination={true}
					paginationPageSize={20}
					getRowId={(p) => p.data.id.toString()}
					cacheBlockSize={100}
					containerStyle={{ height: "70dvh" }}
					className="ag-theme-quartz ag-theme-custom w-100"
					rowSelection="multiple"
					suppressRowClickSelection={true}
					suppressCellFocus={true}
					onGridReady={onGridReady}
					suppressContextMenu
					onSelectionChanged={(e) => {
						const selection = systemStockGridRef.current?.api.getServerSideSelectionState();
						const nodes = selection?.toggledNodes?.map(t => systemStockGridRef.current?.api.getRowNode(t.toString())?.data);
						if(products.selectAllFilter == true){
							const matchingData = products.table.filter(x=> nodes?.find(e=>e.id == x.id));											
							deselectedRowsUpdateAgGrid(matchingData);												
						} else {
							//@ts-ignore
							setServerSideSelectionState(selection && nodes ? {selectAll: selection.selectAll, toggledNodes: nodes} : {selectAll: false, toggledNodes: []});
							selectedRowsUpdateAgGrid(nodes!);
						}
					}}
					onRowSelected={(event: RowSelectedEvent<any, any>) => {
						if(event.source != "uiSelectAll" && products.selectAllFilter == (event.api.getServerSideSelectionState() as IServerSideSelectionState).selectAll){
							return;
						}											
						if((event.api.getServerSideSelectionState() as IServerSideSelectionState).selectAll){
							columnCheckSelectAll();
						} else {
							columnCheckDeselectAll();
						}
					}}
					components={{
						dateCellRenderer: DateCellRenderer
					}}
					
				/>				
			</Modal.Body>
		</Modal>
	);
}